import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  TextField,
  Alert,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Fade,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Category, Add, Refresh, FolderOpen } from '@mui/icons-material';
import PageHeader from '../components/common/PageHeader';
import DataTable from '../components/common/DataTable';
import FormDialog from '../components/common/FormDialog';
import ConfirmDialog from '../components/common/ConfirmDialog';
import StatusBadge from '../components/common/StatusBadge';
import SearchInput from '../components/common/SearchInput';
import FilterBar from '../components/common/FilterBar';
import api from '../services/api';

const initialFormData = {
  name: '',
  code: '',
  retailId: '',
  description: '',
  parentId: '',
  isActive: true,
};

const CategoriesPage = () => {
  const [categories, setCategories] = useState([]);
  const [retails, setRetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [saving, setSaving] = useState(false);
  const [filterRetail, setFilterRetail] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [categoriesRes, retailsRes] = await Promise.all([
        api.get('/categories'),
        api.get('/retails'),
      ]);
      setCategories(categoriesRes.data);
      setRetails(retailsRes.data);
    } catch (err) {
      setError('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenDialog = (category = null) => {
    if (category) {
      setFormData({
        name: category.name,
        code: category.code,
        retailId: category.retailId,
        description: category.description || '',
        parentId: category.parentId || '',
        isActive: category.isActive,
      });
      setSelectedCategory(category);
    } else {
      setFormData({
        ...initialFormData,
        retailId: filterRetail || '',
      });
      setSelectedCategory(null);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setFormData(initialFormData);
    setSelectedCategory(null);
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError('');

    try {
      const payload = {
        ...formData,
        parentId: formData.parentId || null,
      };

      if (selectedCategory) {
        await api.put(`/categories/${selectedCategory.id}`, payload);
        setSuccess('Categoría actualizada exitosamente');
      } else {
        await api.post('/categories', payload);
        setSuccess('Categoría creada exitosamente');
      }
      handleCloseDialog();
      fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar categoría');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/categories/${selectedCategory.id}`);
      setDeleteDialogOpen(false);
      setSelectedCategory(null);
      setSuccess('Categoría eliminada exitosamente');
      fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al eliminar categoría');
    }
  };

  const getRetailName = (retailId) => {
    const retail = retails.find(r => r.id === retailId);
    return retail?.name || '-';
  };

  let filteredCategories = categories;
  if (filterRetail) {
    filteredCategories = filteredCategories.filter(c => c.retailId === filterRetail);
  }
  if (searchTerm) {
    filteredCategories = filteredCategories.filter(c =>
      c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.code.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }

  const columns = [
    { header: 'Nombre', accessor: 'name' },
    { header: 'Código', accessor: 'code' },
    {
      header: 'Retail',
      accessor: 'retailId',
      render: (value) => getRetailName(value),
    },
    {
      header: 'Descripción',
      accessor: 'description',
      render: (value) => value || '-',
      maxWidth: 200,
    },
    {
      header: 'Estado',
      accessor: 'isActive',
      render: (value) => (
        <StatusBadge status={value ? 'active' : 'inactive'} />
      ),
    },
  ];

  const filterOptions = retails.map(r => ({ value: r.id, label: r.name }));

  return (
    <Box>
      <PageHeader
        title="Categorías"
        subtitle="Gestiona las categorías de productos"
        actionLabel="Nueva Categoría"
        actionIcon={<Add />}
        onAction={() => handleOpenDialog()}
        badge={categories.length}
        badgeColor="secondary"
      >
        <Tooltip title="Refrescar lista">
          <IconButton onClick={fetchData}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </PageHeader>

      {error && (
        <Fade in>
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        </Fade>
      )}
      {success && (
        <Fade in>
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        </Fade>
      )}

      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <SearchInput
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Buscar por nombre o código..."
          sx={{ minWidth: 280 }}
        />
        <FormControl sx={{ minWidth: 200 }} size="small">
          <InputLabel>Filtrar por Retail</InputLabel>
          <Select
            value={filterRetail}
            label="Filtrar por Retail"
            onChange={(e) => setFilterRetail(e.target.value)}
          >
            <MenuItem value="">Todos los retails</MenuItem>
            {retails.map((retail) => (
              <MenuItem key={retail.id} value={retail.id}>
                {retail.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <DataTable
        columns={columns}
        data={filteredCategories}
        loading={loading}
        onEdit={handleOpenDialog}
        onDelete={(category) => {
          setSelectedCategory(category);
          setDeleteDialogOpen(true);
        }}
        emptyMessage="No hay categorías registradas"
        emptyDescription="Crea categorías para organizar tus productos"
        emptyIcon={FolderOpen}
        emptyActionLabel="Crear Categoría"
        emptyAction={() => handleOpenDialog()}
      />

      <FormDialog
        open={dialogOpen}
        title={selectedCategory ? 'Editar Categoría' : 'Nueva Categoría'}
        subtitle={selectedCategory ? 'Modifica los datos de la categoría' : 'Ingresa los datos de la nueva categoría'}
        icon={<Category />}
        onClose={handleCloseDialog}
        onSubmit={handleSubmit}
        loading={saving}
      >
        <Grid container spacing={2.5}>
          <Grid item xs={12}>
            <FormControl fullWidth required>
              <InputLabel>Retail</InputLabel>
              <Select
                value={formData.retailId}
                label="Retail"
                onChange={(e) => setFormData({ ...formData, retailId: e.target.value, parentId: '' })}
              >
                {retails.map((retail) => (
                  <MenuItem key={retail.id} value={retail.id}>
                    {retail.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Nombre"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Código"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              fullWidth
              required
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Descripción"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Categoría Padre</InputLabel>
              <Select
                value={formData.parentId}
                label="Categoría Padre"
                onChange={(e) => setFormData({ ...formData, parentId: e.target.value })}
                disabled={!formData.retailId}
              >
                <MenuItem value="">Ninguna (categoría raíz)</MenuItem>
                {categories
                  .filter(c => c.retailId === formData.retailId && c.id !== selectedCategory?.id)
                  .map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label="Categoría Activa"
            />
          </Grid>
        </Grid>
      </FormDialog>

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Eliminar Categoría"
        message={`¿Estás seguro de que deseas eliminar la categoría "${selectedCategory?.name}"?`}
        type="delete"
        onConfirm={handleDelete}
        onCancel={() => setDeleteDialogOpen(false)}
      />
    </Box>
  );
};

export default CategoriesPage;
